import { cameraEventsQuery, siteEventsQuery } from '@/api/queries'
import type { Event } from '@/api/schemas'
import { CameraEventsByHour } from '@/components/camera/CameraEventsByHour'
import { EventsByHour } from '@/components/site/EventsByHour'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import type { SalaIntent } from '@/lib/viewIntent'
import { useQuery } from '@tanstack/react-query'
import { t } from '@/lib/i18n'

type Intent = Extract<SalaIntent, { kind: 'events_by_hour' }>

export function EventsByHourBody({ intent }: { intent: Intent }) {
  if (intent.camera_id) {
    return (
      <CameraVariant
        siteId={intent.site_id}
        cameraId={intent.camera_id}
        date={intent.date}
      />
    )
  }
  return <SiteVariant siteId={intent.site_id} date={intent.date} />
}

function SiteVariant({ siteId, date }: { siteId: string; date: string }) {
  // The site-level /events endpoint does not support a date filter yet;
  // we fetch a generous window and filter client-side. Good enough for
  // the demo scale (one site, a few dozen events a day).
  const q = useQuery(siteEventsQuery(siteId, 500))
  if (q.isPending) return <LoadingCard label={t('Loading events…')} />
  if (q.isError || !q.data) return <ErrorCard message={t('Could not load events.')} />
  const filtered = filterByLocalDate(q.data.events, date)
  return <EventsByHour events={filtered} />
}

function CameraVariant({
  siteId,
  cameraId,
  date,
}: {
  siteId: string
  cameraId: string
  date: string
}) {
  const q = useQuery(cameraEventsQuery(siteId, cameraId, date))
  if (q.isPending) return <LoadingCard label={t('Loading events…')} />
  if (q.isError || !q.data) return <ErrorCard message={t('Could not load events.')} />
  return <CameraEventsByHour events={q.data.events} />
}

function filterByLocalDate(events: Event[], date: string): Event[] {
  // YYYY-MM-DD in the user's local tz. The API stores timestamps in UTC;
  // we format each event's timestamp in the local tz and keep only those
  // whose local date matches. Matches how the dashboard thinks of "today".
  return events.filter((e) => localYmd(e.timestamp) === date)
}

function localYmd(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
